/**
 * Copyright © 2026 Adnuntius AS.
 */

/** Sentinel in {@link visiblePageNumbers} for a gap between numbered pages. */
export const PAGE_ELLIPSIS = 0;

/**
 * Page buttons for a pager. Always includes the first and last page.
 * `0` ({@link PAGE_ELLIPSIS}) marks a gap; templates should render it as an
 * ellipsis and treat it as non-clickable (`page < 1` already no-ops).
 */
export function visiblePageNumbers(current: number, total: number, maxSize = 10): number[] {
  const totalPages = Math.max(1, Math.floor(Number(total)) || 1);
  const currentPage = Math.min(Math.max(1, Math.floor(Number(current)) || 1), totalPages);
  const size = Math.max(3, Math.floor(Number(maxSize)) || 10);

  if (totalPages <= size) {
    return range(1, totalPages);
  }

  const innerMax = size - 2;
  let innerStart = currentPage - Math.floor((innerMax - 1) / 2);
  let innerEnd = innerStart + innerMax - 1;

  if (innerStart < 2) {
    innerEnd += 2 - innerStart;
    innerStart = 2;
  }
  if (innerEnd > totalPages - 1) {
    innerStart -= innerEnd - (totalPages - 1);
    innerEnd = totalPages - 1;
  }
  innerStart = Math.max(2, innerStart);

  const pages: number[] = [1];
  if (innerStart > 2) {
    pages.push(innerStart === 3 ? 2 : PAGE_ELLIPSIS);
  }
  for (let i = innerStart; i <= innerEnd; i++) {
    pages.push(i);
  }
  if (innerEnd < totalPages - 1) {
    pages.push(innerEnd === totalPages - 2 ? totalPages - 1 : PAGE_ELLIPSIS);
  }
  pages.push(totalPages);
  return pages;
}

function range(from: number, to: number): number[] {
  const pages: number[] = [];
  for (let i = from; i <= to; i++) {
    pages.push(i);
  }
  return pages;
}
